/**
 * Geld-Arithmetik. Alle Beträge sind Integer in Cent.
 * Regel aus CLAUDE.md: keine Floats für Beträge, Summe der Anteile == Gesamtbetrag.
 */

export type Cent = number;

export function assertCent(value: number, feld = 'Betrag'): Cent {
  if (!Number.isInteger(value)) {
    throw new Error(`${feld} muss ein Integer in Cent sein, war ${value}`);
  }
  return value;
}

export function euroToCent(euro: number): Cent {
  return Math.round(euro * 100);
}

export function centToEuro(cent: Cent): number {
  return cent / 100;
}

export function formatEuro(cent: Cent): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
    .format(cent / 100);
}

/**
 * Verteilt einen Gesamtbetrag proportional auf Gewichte — verlustfrei.
 *
 * Verfahren: Größte-Reste-Methode (Hare/Niemeyer). Jeder Anteil wird abgerundet,
 * die verbleibenden Restcents gehen an die Positionen mit dem größten Nachkommarest.
 * Bei gleichem Rest entscheidet der niedrigere Index (deterministisch, reproduzierbar).
 *
 * Garantie: sum(result) === gesamt, für jede Eingabe.
 */
export function verteileNachGewichten(gesamt: Cent, gewichte: number[]): Cent[] {
  assertCent(gesamt);
  if (gewichte.length === 0) return [];
  if (gewichte.some((g) => g < 0)) {
    throw new Error('Negative Gewichte sind im Umlageschlüssel nicht zulässig');
  }

  const summe = gewichte.reduce((a, b) => a + b, 0);
  if (summe === 0) {
    // Keine Bemessungsgrundlage: nichts verteilbar. Der Aufrufer muss das behandeln
    // (z.B. Kostenart ohne Verbrauchsdaten -> Warnung statt stiller Nullverteilung).
    return gewichte.map(() => 0);
  }

  const exakt = gewichte.map((g) => (gesamt * g) / summe);
  const abgerundet = exakt.map((x) => Math.floor(x));
  let rest = gesamt - abgerundet.reduce((a, b) => a + b, 0);

  const reihenfolge = exakt
    .map((x, i) => ({ i, rest: x - Math.floor(x) }))
    .sort((a, b) => (b.rest - a.rest) || (a.i - b.i));

  const ergebnis = [...abgerundet];
  // Bei negativem Gesamtbetrag (Gutschrift) läuft der Rest in die andere Richtung.
  const schritt = rest >= 0 ? 1 : -1;
  let k = 0;
  while (rest !== 0) {
    ergebnis[reihenfolge[k % reihenfolge.length].i] += schritt;
    rest -= schritt;
    k++;
  }
  return ergebnis;
}

/** Prozentuale Aufteilung eines Betrags in zwei Teile, verlustfrei. */
export function teileProzent(gesamt: Cent, prozentA: number): { a: Cent; b: Cent } {
  const [a, b] = verteileNachGewichten(gesamt, [prozentA, 100 - prozentA]);
  return { a, b };
}
